import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryClient } from '@/configuration/react-query';
import { AssetScope, AssetType, ORGANIZATION_PERMISSION_TYPE } from '@/enums';
import { BlobLocation } from '@/enums/BlobLocationEnum';
import {
  addThumbnailToAssetPayload,
  buildUploadedAssetPayload,
  coreUpload,
  CoreUploadContext,
  getValidationFormatsFromPickerTypes,
  isStockClipUploadType,
  sortUploadFilesByName
} from '@/helpers';
import { showToast, showUploadSummaryToast, ToastType } from '@/helpers/toast';
import {
  useCalculateOrganizationStorageMutation,
  useCreateAssetMutation,
  useCreateSasTokenMutation,
  useGetOrganizationByIdSimplifiedQuery,
  useUpdateAsset
} from '@/services';
import { useCreateStockClipMutation } from '@/services/stockClip.service';
import {
  Asset,
  AssetUploadErrorType,
  CreateEditAssetPayload,
  FilePickerType,
  UploadErrorDetail
} from '@/types';
import { CreateStockClipPayload } from '@/types/StockClipTypes';

type UseFileUploadOptions = {
  pickerTypes: FilePickerType[];

  eventId?: string | number | null;
  organizationId?: string | number | null;
  themeId?: number | null;
  isPublic?: boolean;
  assetScope?: AssetScope;
  assetTag?: string | string[];
  blobLocation?: BlobLocation;

  uploadAssetType?: AssetType | string;
  activeAssetId?: number | null;

  maxFiles?: number;
  sortFiles?: boolean;
  showSummaryToast?: boolean;

  onCreatedAsset?: (asset: Asset) => void;
  onUploadedAsset?: (asset: Asset) => void;
  onUploadComplete?: (assetId?: number) => void;
  onRefetchNeeded?: () => void;
  onUploadingChange?: (isUploading: boolean) => void;
};

type ProcessFileUploadResult = {
  asset?: Asset;
  error?: UploadErrorDetail;
};

type UploadFileToBlobResult = {
  assetUrl?: string;
  error?: UploadErrorDetail;
};

type UploadFilesResult = {
  uploadedCount: number;
  failedCount: number;
  totalCount: number;
  errors: UploadErrorDetail[];
  assets: Asset[];
};

type StorageGuard = {
  storageLimit?: number;
  storageUsed: number;
  isTimeLimitExceeded: boolean;
};

export const useFileUpload = ({
  pickerTypes,
  eventId,
  organizationId,
  themeId,
  isPublic = false,
  assetScope,
  assetTag,
  blobLocation,
  uploadAssetType,
  activeAssetId = null,
  maxFiles,
  sortFiles = true,
  showSummaryToast = true,
  onCreatedAsset,
  onUploadedAsset,
  onUploadComplete,
  onRefetchNeeded,
  onUploadingChange
}: UseFileUploadOptions) => {
  const [t] = useTranslation('common');
  const [isUploading, setIsUploading] = useState(false);
  const normalizedOrganizationId = organizationId ? Number(organizationId) : 0;

  const { refetch: refetchOrganizationById } =
    useGetOrganizationByIdSimplifiedQuery(normalizedOrganizationId, undefined, false);

  const validationFormats = useMemo(
    () => getValidationFormatsFromPickerTypes(pickerTypes),
    [pickerTypes]
  );

  const updateUploadingState = (value: boolean) => {
    setIsUploading(value);
    onUploadingChange?.(value);
  };

  const handleUploadSuccess = (asset?: Asset, dispatchUploadEvent = true) => {
    if (!asset) {
      return;
    }

    onCreatedAsset?.(asset);
    onUploadComplete?.(asset.id);
    onRefetchNeeded?.();

    if (dispatchUploadEvent) {
      document.dispatchEvent(
        new CustomEvent('uploadComplete', {
          detail: {
            id: asset.id
          }
        })
      );
    }
  };

  const { mutateAsync: createSasToken } = useCreateSasTokenMutation(queryClient, {
    onSuccess: () => {
      // Handled by upload result summary
    },
    onError: () => {
      // Handled by upload result summary
    }
  });

  const { mutateAsync: createAsset } = useCreateAssetMutation(queryClient, {
    onSuccess: (response) => {
      handleUploadSuccess(response?.data as Asset);
    },
    onError: () => {
      // Handled by upload result summary
    }
  });

  const { mutateAsync: updateAsset } = useUpdateAsset(queryClient, {
    onSuccess: (response) => {
      handleUploadSuccess(response?.data as Asset, false);
    },
    onError: () => {
      // Handled by upload result summary
    }
  });

  const { mutateAsync: createStockClip } = useCreateStockClipMutation(queryClient, {
    onSuccess: (response) => {
      handleUploadSuccess(response?.data.stockClipAsset as Asset);
    },
    onError: () => {
      // Handled by upload result summary
    }
  });

  const { mutateAsync: calculateOrganizationStorage } = useCalculateOrganizationStorageMutation(
    queryClient,
    {
      onSuccess: () => {
        // Storage is recalculated after upload to keep organization usage in sync.
      },
      onError: () => {
        // Upload result should not fail if storage recalculation fails.
      }
    }
  );

  const persistUploadedAsset = async (
    assetPayload: CreateEditAssetPayload
  ): Promise<Asset | undefined> => {
    if (activeAssetId) {
      const response = await updateAsset({
        ...assetPayload,
        id: activeAssetId
      });

      return response?.data as unknown as Asset | undefined;
    }

    if (isStockClipUploadType(uploadAssetType)) {
      const payload: CreateStockClipPayload = {
        stockClipAsset: assetPayload
      };

      const response = await createStockClip(payload);
      return response?.data?.stockClipAsset as Asset | undefined;
    }

    const response = await createAsset(assetPayload);

    return response?.data as unknown as Asset | undefined;
  };

  const getCoreUploadContext = (): CoreUploadContext => ({
    validationFormats,
    uploadAssetType,
    assetScope,
    eventId,
    organizationId,
    isPublic,
    createSasToken
  });

  const getStorageGuard = async (): Promise<StorageGuard | null> => {
    if (!normalizedOrganizationId) {
      return null;
    }

    try {
      const organizationResponse = await refetchOrganizationById();
      const organizationData = organizationResponse.data?.data;
      const permissions =
        organizationData?.paymentPlan?.organizationPermissions
          ?.map((permission: { name?: string }) => permission?.name)
          .filter(Boolean) ?? [];

      const hasUnlimitedStorage = permissions.includes(
        ORGANIZATION_PERMISSION_TYPE.UNLIMITED_STORAGE
      );
      const hasUnlimitedTime = permissions.includes(ORGANIZATION_PERMISSION_TYPE.UNLIMITED_TIME);
      const nextPaymentDate = organizationData?.organizationSettings?.nextPaymentDate;
      const parsedNextPaymentDate = nextPaymentDate ? new Date(nextPaymentDate) : null;
      const isTimeLimitExceeded =
        !hasUnlimitedTime &&
        !!parsedNextPaymentDate &&
        !Number.isNaN(parsedNextPaymentDate.getTime()) &&
        parsedNextPaymentDate < new Date();

      const storageLimit = organizationData?.organizationSettings?.storageLimit;
      const storageUsed = organizationData?.organizationSettings?.storageUsed;

      if (hasUnlimitedStorage) {
        return isTimeLimitExceeded
          ? {
              storageUsed: typeof storageUsed === 'number' ? storageUsed : 0,
              isTimeLimitExceeded
            }
          : null;
      }

      if (typeof storageLimit !== 'number' || storageLimit <= 0) {
        return isTimeLimitExceeded
          ? {
              storageUsed: typeof storageUsed === 'number' ? storageUsed : 0,
              isTimeLimitExceeded
            }
          : null;
      }

      return {
        storageLimit,
        storageUsed: typeof storageUsed === 'number' ? storageUsed : 0,
        isTimeLimitExceeded
      };
    } catch {
      return null;
    }
  };

  const uploadFileToBlob = async (file: File): Promise<UploadFileToBlobResult> => {
    updateUploadingState(true);
    try {
      const result = await coreUpload(file, getCoreUploadContext(), { blobLocation, themeId });
      return result.error ? { error: result.error } : { assetUrl: result.assetUrl };
    } catch {
      return {
        error: { assetName: file.name, errorType: AssetUploadErrorType.GENERIC_UPLOAD_ERROR }
      };
    } finally {
      updateUploadingState(false);
    }
  };

  const processFileUpload = async (file: File): Promise<ProcessFileUploadResult> => {
    try {
      const result = await coreUpload(file, getCoreUploadContext());

      if (result.error) {
        return { error: result.error };
      }

      const { fileToUpload, duration, assetType, assetUrl, sasAssetName } = result;

      const baseAssetPayload = buildUploadedAssetPayload({
        file: fileToUpload,
        assetUrl,
        assetType,
        eventId,
        organizationId,
        isPublic,
        assetScope,
        assetTag,
        duration
      });

      const assetPayload = await addThumbnailToAssetPayload({
        assetPayload: baseAssetPayload,
        assetScope,
        assetType,
        createSasToken,
        eventId,
        isPublic,
        organizationId,
        originalFile: fileToUpload,
        originalSasAssetName: sasAssetName
      });

      const createdAsset = await persistUploadedAsset(assetPayload);

      if (!createdAsset?.id) {
        return {
          error: { assetName: file.name, errorType: AssetUploadErrorType.GENERIC_UPLOAD_ERROR }
        };
      }

      onUploadedAsset?.(createdAsset);

      return { asset: createdAsset };
    } catch {
      return {
        error: { assetName: file.name, errorType: AssetUploadErrorType.GENERIC_UPLOAD_ERROR }
      };
    }
  };

  const uploadFiles = async (files: File[] | FileList): Promise<UploadFilesResult> => {
    const filesToUpload = sortFiles ? sortUploadFilesByName(files) : Array.from(files);

    const initialResult: UploadFilesResult = {
      uploadedCount: 0,
      failedCount: 0,
      totalCount: filesToUpload.length,
      errors: [],
      assets: []
    };

    if (filesToUpload.length === 0) {
      return initialResult;
    }

    if (maxFiles && filesToUpload.length > maxFiles) {
      showToast(ToastType.ERROR, t('uploadLimited', { maxAssets: maxFiles }));

      return {
        ...initialResult,
        failedCount: filesToUpload.length,
        errors: filesToUpload.map((file) => ({
          assetName: file.name,
          errorType: AssetUploadErrorType.GENERIC_UPLOAD_ERROR
        }))
      };
    }

    const storageGuard = await getStorageGuard();

    if (storageGuard?.isTimeLimitExceeded) {
      const errors = filesToUpload.map((file) => ({
        assetName: file.name,
        errorType: AssetUploadErrorType.TIME_LIMIT_EXCEEDED
      }));

      if (showSummaryToast) {
        showUploadSummaryToast(
          {
            uploadedCount: 0,
            failedCount: filesToUpload.length,
            totalCount: filesToUpload.length,
            errors
          },
          t
        );
      }

      return {
        ...initialResult,
        failedCount: filesToUpload.length,
        errors
      };
    }

    updateUploadingState(true);

    try {
      const result: UploadFilesResult = {
        ...initialResult
      };
      let currentStorageUsed = storageGuard?.storageUsed ?? 0;

      for (const file of filesToUpload) {
        if (
          typeof storageGuard?.storageLimit === 'number' &&
          currentStorageUsed + file.size > storageGuard.storageLimit
        ) {
          result.failedCount++;
          result.errors.push({
            assetName: file.name,
            errorType: AssetUploadErrorType.STORAGE_LIMIT_EXCEEDED
          });
          continue;
        }

        const uploadResult = await processFileUpload(file);

        if (uploadResult.asset) {
          result.uploadedCount++;
          result.assets.push(uploadResult.asset);
          if (typeof storageGuard?.storageLimit === 'number') {
            currentStorageUsed += uploadResult.asset.assetSize ?? file.size;
          }
          continue;
        }

        if (uploadResult.error) {
          result.failedCount++;
          result.errors.push(uploadResult.error);
        }
      }

      if (showSummaryToast) {
        showUploadSummaryToast(
          {
            uploadedCount: result.uploadedCount,
            failedCount: result.failedCount,
            totalCount: result.totalCount,
            errors: result.errors
          },
          t
        );
      }

      if (typeof storageGuard?.storageLimit === 'number' && result.uploadedCount > 0) {
        try {
          await calculateOrganizationStorage(normalizedOrganizationId);
        } catch {
          // Upload result should not fail if storage recalculation fails.
        }
      }

      return result;
    } catch {
      showToast(ToastType.ERROR, t('uploadFailed'));

      return {
        ...initialResult,
        failedCount: filesToUpload.length,
        errors: filesToUpload.map((file) => ({
          assetName: file.name,
          errorType: AssetUploadErrorType.GENERIC_UPLOAD_ERROR
        }))
      };
    } finally {
      updateUploadingState(false);
    }
  };

  return {
    isUploading,
    uploadFiles,
    uploadFileToBlob
  };
};
